import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useUserType } from '@/hooks/useUserType';

interface RequireOrganizerProps {
  children: React.ReactNode;
}

/**
 * Bounces GUEST users away from organizer-only pages (Dashboard, Eventos list,
 * Cuenta, Admin, Providers directory, Super admin). Guests get redirected
 * straight to their assigned event.
 */
export const RequireOrganizer: React.FC<RequireOrganizerProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { userType, isGuest, accessibleEventId, loading: typeLoading } = useUserType();
  const location = useLocation();

  if (authLoading || typeLoading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isGuest) {
    if (accessibleEventId) {
      return <Navigate to={`/app/events/${accessibleEventId}/guests`} replace />;
    }
    // Guest with no active access — nothing we can show them.
    return <Navigate to="/login" replace />;
  }

  // userType might still be null briefly; treat unknown as organizer (the
  // RLS layer is the actual fence).
  if (userType && userType !== 'ORGANIZER') {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};
